import { apiClient } from '@indipia/shared/api/client';
import { API_PRX } from '@indipia/shared/api/endpoints';

/**
 * 반장 입고 사진 등록 — 외부·무인증 공유 화면 API (WorkLog ReceiptShareController).
 * 경로가 `/worklog/receipt-shares/me` 로 시작해 client.ts AUTH_EXEMPT_PATHS 에 걸린다(공유 쿠키만 사용).
 * shareId 는 경로·본문·쿼리 어디에도 싣지 않는다(F-04 §9).
 */

/** 제출 업로드 timeout (ms) — 사진 5장 x 20MB 기준 초기값. 느린 회선 시험 후 조정(지시서 §13 M10). */
export const RECEIPT_SUBMIT_TIMEOUT_MS = 300000;

export type ReceiptShareState = 'OPEN' | 'RECEIVED' | 'CLOSED' | 'EXPIRED';

export interface ReceiptShareItem {
  itemSeq?: number;
  itemName: string;
  itemSpec?: string | null;
  itemQty?: number | string | null;
  itemUnit?: string | null;
}

export interface ReceiptShareTarget {
  vendorName?: string | null;
  companyName?: string | null;
  siteLabel?: string | null;
  workTypeName?: string | null;
  deliveryAddress?: string | null;
  deliveryDate?: string | null;
  items?: ReceiptShareItem[] | null;
  liftReqYn?: string | null;
  arrivalDate?: string | null;
  arrivalTime?: string | null;
  driverName?: string | null;
  driverPhone?: string | null;
  foremanName?: string | null;
}

export interface ReceiptShareReceipt {
  receiptDtm?: string | null;
  receiptDate?: string | null;
  receiptMethodCode?: string | null;
  photoCnt?: number | null;
  qtyCheckedCode?: string | null;
}

export interface ReceiptShareView {
  state?: string | null;
  closedReasonCode?: string | null;
  expiresDtm?: string | null;
  target?: ReceiptShareTarget | null;
  receipt?: ReceiptShareReceipt | null;
}

export interface ReceiptSubmitResult {
  state?: string | null;
  receiptDate?: string | null;
  photoCnt?: number | null;
  qtyCheckedCode?: string | null;
}

interface Envelope<T> {
  success?: boolean;
  code?: string;
  message?: string;
  data: T;
}

export const receiptShareApi = {
  /** 입고 링크 조회 — R9(READ). 5초 규칙(D-07 §3.2 #11). */
  getReceiptShare(timeoutMs: number, signal?: AbortSignal) {
    return apiClient.get<Envelope<ReceiptShareView>>(`${API_PRX.WORKLOG}/receipt-shares/me`, {
      timeout: timeoutMs,
      signal,
    });
  },

  /**
   * 입고 제출 — W8(RESPOND). multipart. Content-Type 은 직접 지정하지 않는다(boundary 보존).
   * 처음 201, 이미 반영됐으면 200 — 둘 다 성공으로 취급한다.
   */
  submitReceipt(form: FormData, onProgress: (percent: number) => void, signal?: AbortSignal) {
    return apiClient.post<Envelope<ReceiptSubmitResult>>(`${API_PRX.WORKLOG}/receipt-shares/me/receipts`, form, {
      timeout: RECEIPT_SUBMIT_TIMEOUT_MS,
      signal,
      onUploadProgress: (e) => {
        if (e.total && e.total > 0) onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
      },
    });
  },
};
